const CAMERA_KEYS = new Set([
  'number_of_cameras',
  'resolution',
  'rear_camera_resolution',
  'left_and_right_cameras_resolution',
  'frame_rate',
  'viewing_angle',
  'rear_camera_viewing_angle',
  'left_and_right_cameras_viewing_angle',
  'sensor_size',
  'night_vision',
  'adjustable_camera_lens',
]);

const FEATURE_KEYS = new Set([
  'mobile_app_control',
  'voice_control',
  'gps',
  'wifi',
  'bluetooth',
  'adas',
  'parking_monitoring',
]);

const DOORBELL_FEATURE_KEYS = new Set([
  'mobile_app_control',
  'wifi',
  'video_modes',
  'motion_detection',
  'night_vision',
  'audio',
  'doorbell_button',
  'smart_home_compatibility',
]);

const OTHER_KEYS = new Set([
  'display',
  'memory_capacity',
  'lithium_battery',
  'duration',
  'communication_port',
  'dimensions',
  'power_cable_length',
  'rear_camera_cable_length',
  'car_charger',
  'warranty',
]);

class RespectoProductAccordion extends HTMLElement {
  #transitions = new Map();
  #scrollController;
  connectedCallback() {
    if (this.dataset.enhanced === 'true') return;

    if (this.dataset.respectoMode === 'split' && this.dataset.respectoRendered !== 'server') {
      this.#splitSpecificationRows();
    }

    this.querySelectorAll('details').forEach((item) => this.#bindAnimation(item));
    this.dataset.enhanced = 'true';
  }

  #splitSpecificationRows() {
    const source = this.querySelector('[data-respecto-source]');
    const groups = this.querySelector('[data-respecto-groups]');
    const rows = source ? Array.from(source.querySelectorAll('tr')) : [];

    if (!source || !groups || rows.length === 0) return;

    const buckets = { camera: [], features: [], other: [], warranty: [] };

    for (const row of rows) {
      const key = this.#rowKey(row);
      let group = 'other';

      if (CAMERA_KEYS.has(key)) group = 'camera';
      const featureKeys = this.dataset.respectoProductKind === 'doorbell'
        ? DOORBELL_FEATURE_KEYS
        : FEATURE_KEYS;

      if (featureKeys.has(key)) group = 'features';
      if (OTHER_KEYS.has(key)) group = 'other';

      if (key === 'warranty') {
        buckets.warranty.push(row);
      } else {
        buckets[group].push(row);
      }
    }

    buckets.other.push(...buckets.warranty);

    for (const group of ['camera', 'features', 'other']) {
      const body = groups.querySelector(`[data-respecto-rows="${group}"]`);
      const item = groups.querySelector(`[data-respecto-group="${group}"]`);
      if (!body || !item) continue;

      if (buckets[group].length === 0) {
        item.hidden = true;
      } else {
        body.append(...buckets[group]);
        body.append(...body.querySelectorAll('[data-respecto-fixed-last]'));
      }
    }

    source.hidden = true;
    groups.hidden = false;
  }

  disconnectedCallback() {
    this.#scrollController?.abort();
    for (const state of [...this.#transitions.values()]) state.finish();
  }

  #bindAnimation(item) {
    const summary = item.querySelector(':scope > summary');
    const panel = item.querySelector(':scope > .respecto-dashcam-accordion__panel');
    if (!summary || !panel) return;

    summary.addEventListener('click', (event) => {
      event.preventDefault();
      this.#scrollController?.abort();
      const opening = !(this.#transitions.get(item)?.opening ?? item.open);
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const siblings = Array.from(this.querySelectorAll('.respecto-dashcam-accordion__item'))
        .filter((other) => other !== item && other.closest('respecto-dashcam-accordion') === this);

      if (opening) {
        const openSiblings = siblings.filter((other) => this.#transitions.get(other)?.opening ?? other.open);
        if (openSiblings.some((other) => other.getBoundingClientRect().top < item.getBoundingClientRect().top)) {
          this.#keepSummaryPosition(summary);
        }
        for (const other of openSiblings) {
          const otherPanel = other.querySelector(':scope > .respecto-dashcam-accordion__panel');
          if (otherPanel) this.#animate(other, otherPanel, false, reducedMotion);
        }
      }

      this.#animate(item, panel, opening, reducedMotion);
    });
  }

  #keepSummaryPosition(summary) {
    const top = summary.getBoundingClientRect().top;
    if (top < 0 || top > window.innerHeight) return;
    const controller = new AbortController();
    this.#scrollController = controller;
    let frame;
    const stop = () => controller.abort();
    for (const type of ['wheel', 'touchstart', 'pointerdown', 'keydown']) {
      window.addEventListener(type, stop, { passive: true, signal: controller.signal });
    }
    controller.signal.addEventListener('abort', () => window.cancelAnimationFrame(frame), { once: true });
    const follow = () => {
      if (controller.signal.aborted || !summary.isConnected) return;
      const delta = summary.getBoundingClientRect().top - top;
      if (Math.abs(delta) > 0.5) window.scrollBy({ top: delta, behavior: 'instant' });
      if (this.#transitions.size) frame = window.requestAnimationFrame(follow);
      else stop();
    };
    frame = window.requestAnimationFrame(follow);
  }

  #animate(item, panel, opening, reducedMotion) {
    const height = item.open ? panel.getBoundingClientRect().height : 0;
    const style = item.open ? window.getComputedStyle(panel) : null;
    const opacity = style?.opacity || '0';
    const transform = style?.transform || 'translateY(-4px)';
    this.#transitions.get(item)?.cancel();
    item.dataset.animating = 'true';
    item.open = true;
    let frame;
    let timeout;
    let completed = false;
    const cleanup = () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timeout);
      panel.removeEventListener('transitionend', onTransitionEnd);
      this.#transitions.delete(item);
    };
    const finish = () => {
      if (completed) return;
      completed = true;
      cleanup();
      item.open = opening;
      for (const property of ['height', 'opacity', 'transform', 'transition']) panel.style.removeProperty(property);
      delete item.dataset.animating;
    };
    const onTransitionEnd = (event) => {
      if (event.target === panel && event.propertyName === 'height') finish();
    };
    this.#transitions.set(item, { opening, cancel: cleanup, finish });
    if (reducedMotion) {
      finish();
      return;
    }
    panel.style.transition = 'none';
    panel.style.height = `${height}px`;
    panel.style.opacity = opacity;
    panel.style.transform = transform;
    panel.getBoundingClientRect();
    panel.style.removeProperty('transition');
    panel.addEventListener('transitionend', onTransitionEnd);
    frame = window.requestAnimationFrame(() => {
      panel.style.height = opening ? `${panel.scrollHeight}px` : '0px';
      panel.style.opacity = opening ? '1' : '0';
      panel.style.transform = opening ? 'translateY(0)' : 'translateY(-3px)';
    });
    timeout = window.setTimeout(finish, 440);
  }

  #rowKey(row) {
    const image = row.querySelector('img');
    if (image?.dataset.respectoKey) return image.dataset.respectoKey;
    const source = image?.currentSrc || image?.getAttribute('src') || '';

    if (source) {
      try {
        const filename = new URL(source, document.baseURI).pathname.split('/').pop() || '';
        return decodeURIComponent(filename)
          .replace(/\.(png|jpe?g|webp|svg)$/i, '')
          .toLowerCase();
      } catch {
        // Use the translated label when an image URL is unavailable.
      }
    }

    const label = (row.cells?.[0]?.textContent || '').trim().toLowerCase();
    if (label.includes('გარანტია') || label.includes('warranty')) return 'warranty';
    if (label.includes('კამერ') || label.includes('camera') || label.includes('resolution')) return 'resolution';
    if (label.includes('wifi') || label.includes('wi-fi')) return 'wifi';
    if (label.includes('gps')) return 'gps';
    if (label.includes('პარკირ') || label.includes('parking')) return 'parking_monitoring';
    return '';
  }
}

if (!customElements.get('respecto-dashcam-accordion')) {
  customElements.define('respecto-dashcam-accordion', RespectoProductAccordion);
}
